// Live job descriptions. Source of truth: JD_CriticalBridge_*.docx (July 2026).
// Update `updatedAt` when you edit copy. Comp bands are TBD in the source JDs.

export type EmploymentType = 'Full-time' | 'Part-time' | 'Contract' | 'Internship';

export interface RoleCompensation {
  min?: number;
  max?: number;
  currency?: string;
  equity?: boolean;
  equityNote?: string;
  salaryNote?: string;
  benefits?: string[];
  notes?: string;
}

export interface RoleStackGroup {
  label: string;
  items: string;
}

export interface Role {
  id: string;
  title: string;
  department: string;
  location: string;
  remote: boolean;
  employmentType: EmploymentType;
  tagline?: string;
  about?: string[];
  summary: string;
  responsibilities: string[];
  requirements: string[];
  niceToHave?: string[];
  stack?: RoleStackGroup[];
  /** Prose alternative to the labelled `stack` groups; both may be set. */
  stackSummary?: string;
  stackHeading?: string;
  howWeWork?: string;
  compensation?: RoleCompensation;
  updatedAt: string;
}

export const roles: Role[] = [
  {
    id: 'cto',
    title: 'Chief Technology Officer',
    department: 'Leadership',
    location: 'New York, NY (Hybrid: 3 days on-site, 2 days remote)',
    remote: false,
    employmentType: 'Full-time',
    tagline:
      "Own the technology that lets the world's most regulated enterprises trust AI agents in production.",
    about: [
      'CriticalBridge is an enterprise platform for building, governing, and running AI agents and automated workflows in regulated industries — starting with insurance, expanding to healthcare and finance.',
      'Every large enterprise is now accumulating AI agents faster than it can govern or connect them — built in different frameworks, owned by different teams, running as isolated silos. Agent sprawl has become a board-level problem, and new regulations carry hard deadlines. CriticalBridge is the neutral control plane that lets organizations design, deploy, govern, and orchestrate agents — native and third-party — inside their own security perimeter, with the audit trail and compliance controls regulated buyers require.',
      "We're a seed-stage company founded by operators who have led underwriting, IT, and large-scale transformation inside the world's largest carriers. We have live pilots underway with Tier-1 global insurance carriers. The product is real, in customers' hands, and the market timing is now.",
    ],
    summary:
      "This is the founding engineering leadership hire. You'll own the platform architecture end to end, build and lead the engineering team, and stay deeply hands-on — in the design and in the code — through our first wave of enterprise deployments. You'll also be in the room for technical due diligence with carrier CIOs and Chief AI Architects — the people deciding whether to put us into production. You'll report to the CEO and work side by side with them to set and execute the platform strategy, as a core member of the leadership team.",
    responsibilities: [
      'Personally own the end-to-end platform architecture.',
      "Stay hands-on. You'll spend significant time designing, writing, and reviewing code — not just directing it. You'll know the platform down to the codeline, and you set the bar by example.",
      'Hire, lead, and mentor the engineering team, and build the culture and processes that scale with us.',
      "Own the security and compliance posture for regulated verticals: NAIC-aligned controls, SOC 2 readiness, and compliance-grade governance — validation gates, human-in-the-loop enforcement, and audit infrastructure that survives a carrier's risk review.",
      'Drive delivery of our active pilots — turning scoping sessions into production deployments inside complex, legacy-heavy carrier environments.',
      'Own the agentic execution platform: agent orchestration, the LLM provider-abstraction layer, RAG and knowledge-graph retrieval, and the governance layer wrapping all of it.',
      'Make the build/buy/vendor calls, manage cloud cost and reliability, and own our GCP footprint.',
      'Work directly with the CEO to set and execute the platform strategy; partner with founders and customers on roadmap and technical due diligence.',
    ],
    requirements: [
      'A sitting or recent CTO / VP Engineering from a SaaS software company, with a track record of personally architecting, building, and shipping commercial software platforms.',
      'Strong systems-architecture skills: you have personally designed and evolved cloud-native, multi-tenant SaaS platforms — service boundaries, tenant-isolation models, data architecture, API design — and can whiteboard and defend every major decision.',
      'Deep coding experience — current, not historical. Expert in TypeScript/Node.js; you still write production code, review PRs critically, and expect to own the platform architecture and every line of code that ships. Google Cloud experience strongly preferred.',
      'Real distributed-systems chops: queues, event-driven coordination, isolation models, data consistency, observability, and scale.',
      'Experience building for regulated or security-sensitive enterprises (insurance, finance, healthcare, or similar) would be helpful — SOC 2 / compliance, enterprise SSO, audit, and data-isolation requirements.',
      'Real AI product experience — you have built and shipped AI-enabled products that run in production for real users, not just used AI tools or prototyped demos. Hands-on with AI/LLM systems: agent orchestration, RAG, embeddings/vector search, prompt and eval pipelines, provider abstraction, and the reliability and cost trade-offs of running LLMs in production.',
      'Comfort being customer-facing in technical settings — you can earn the trust of a skeptical CIO or Chief AI Architect.',
      "Startup or 0→1 experience; thrives in ambiguity, sets priorities, and ships under real deadlines. You'd rather build the platform and be accountable for it than administer it.",
    ],
    stackSummary:
      "TypeScript (strict) / Node.js on Google Cloud — Cloud Run, Pub/Sub, Postgres, BigQuery — with Terraform IaC. Electron/React desktop application and Next.js web. AI-native architecture: RAG over vector search plus a knowledge graph, and provider-abstracted LLMs (OpenAI / Anthropic / Google). OpenTelemetry-based observability throughout. We'll walk you through the full architecture during the interview process.",
    compensation: {
      min: 200000,
      max: 275000,
      currency: 'USD',
      equity: true,
      equityNote: 'Meaningful equity from the first option pool.',
      salaryNote:
        "The final base salary offer will depend on the candidate's geographic location, experience, and skill level.",
      benefits: [
        'Comprehensive health, dental, and vision insurance',
        'Paid Time Off (PTO) and company holidays',
      ],
    },
    updatedAt: '2026-09-11',
  },
  {
    id: 'senior-engineer-backend',
    title: 'Senior Software Engineer — Backend / Platform',
    department: 'Engineering',
    location: 'Hybrid (NYC preferred) / Remote (US)',
    remote: true,
    employmentType: 'Full-time',
    tagline:
      "Build the per-entity coordination engine that runs and governs AI agents for the world's largest regulated enterprises.",
    about: [
      "CriticalBridge is an enterprise platform for building, governing, and running AI agents and automated workflows in regulated industries — starting with insurance. Every large enterprise is now accumulating AI agents faster than it can govern or connect them, and that sprawl has become a board-level problem with hard regulatory deadlines attached. We're the neutral control plane that lets organizations design, deploy, and govern agents — native and third-party — inside their own security perimeter.",
      "We're seed-stage, founded by operators who've led IT and underwriting inside the world's largest carriers, with live pilots underway at Tier-1 global insurers. You'd be one of our first engineering hires, with foundational ownership of the platform.",
    ],
    summary:
      "You'll own and extend the Brain — the per-entity cloud platform that coordinates, executes, governs, and audits every automation. Our defining architectural rule is one Brain per customer: each tenant gets a dedicated, fully isolated environment — no shared compute, data, or messaging. You'll work across the control plane, the agent execution runtime, the document-ingestion pipeline, and the distributed substrate underneath them.",
    howWeWork:
      'TypeScript strict mode with zero-error gates. One-issue / one-branch / one-PR, with small, focused PRs (~10 files / 500 lines) and short-lived branches. Branch-protected main with required typecheck gates. Pre-commit linting and formatting (husky, lint-staged, ESLint, Prettier). Security and auditability are first-class, not afterthoughts.',
    responsibilities: [
      "Build and scale the Brain's control plane: agent registry, work-assignment queue and routing, process orchestration, approval escalation, the event backbone, and the audit log.",
      'Own the agent execution runtime (cloud and local placement) and the ingestion pipeline that parses documents, extracts entities, and builds the knowledge graph.',
      'Strengthen our multi-tenant isolation model across messaging, identity, secrets, and the data layer — isolation is a feature our customers pay for.',
      'Build the compliance and governance infrastructure: validation gates, human-in-the-loop enforcement, and tamper-evident audit built to satisfy regulated buyers.',
      'Work across the data layer — Cloud SQL (Postgres) coordination ledgers, GCS artifacts, Redis caching, pgVector RAG, and the Neo4j knowledge graph — and the LLM provider-abstraction layer.',
      'Own reliability, observability, and cost: distributed tracing, alerting, Cloud Run concurrency tuning, and the per-entity scaling levers.',
      'Ship infrastructure as code (Terraform) and CI/CD through Cloud Build / Cloud Deploy / Artifact Registry.',
    ],
    requirements: [
      "8+ years building production backend systems, with strong TypeScript/Node.js (we'll consider strong engineers from other typed backgrounds who are eager to go deep in TS).",
      'Solid distributed-systems fundamentals: queues, event-driven systems, idempotency, concurrency, data consistency, and failure handling.',
      'Hands-on cloud-native experience — Google Cloud strongly preferred (Cloud Run, Pub/Sub, Cloud SQL, IAM, Secret Manager), but strong AWS/Azure backgrounds welcome.',
      'Comfort with Postgres at depth, plus IaC (Terraform) and CI/CD pipelines.',
      'A bias toward correctness, observability, and clean, well-tested code — you sweat the edge cases because our customers are regulated.',
    ],
    niceToHave: [
      'Multi-tenant SaaS isolation, enterprise security, or compliance (SOC 2, regulated verticals).',
      'AI/LLM systems: agent orchestration, RAG, embeddings/vector search, eval pipelines.',
      'Knowledge graphs (Neo4j), pgVector, or document-ingestion DAGs.',
      'Insurance, finance, or healthcare domain exposure.',
    ],
    stack: [
      { label: 'Language', items: 'TypeScript (strict), Node.js' },
      {
        label: 'Cloud (GCP-native)',
        items:
          'Cloud Run + Cloud Run Jobs, Pub/Sub, Cloud Tasks, Cloud Scheduler, Cloud Build, Cloud Deploy, Artifact Registry, Cloud SQL (Postgres), Cloud Storage, BigQuery, Memorystore (Redis), Secret Manager, Cloud KMS, IAM, API Gateway',
      },
      { label: 'IaC', items: 'Terraform' },
      {
        label: 'Data & AI',
        items:
          'Supabase (Postgres + pgVector) for RAG, Neo4j Aura knowledge graph, Vertex AI embeddings, Gemini, provider-abstracted LLMs (OpenAI / Anthropic / Google)',
      },
      { label: 'Identity', items: 'Auth0 (SSO/SAML, OAuth 2.0 / PKCE)' },
      {
        label: 'Observability',
        items: 'OpenTelemetry → Cloud Trace, Cloud Monitoring, Cloud Logging → SIEM',
      },
    ],
    compensation: {
      min: 170000,
      max: 210000,
      currency: 'USD',
      equity: true,
      equityNote: 'Meaningful equity from the first option pool',
      salaryNote:
        "The final base salary offer will depend on the candidate's geographic location, experience, and skill level.",
      benefits: [
        'Comprehensive health, dental, and vision insurance',
        'Paid Time Off (PTO) and company holidays',
      ],
    },
    updatedAt: '2026-07-20',
  },
  {
    id: 'senior-engineer-fullstack',
    title: 'Senior Software Engineer — Full-Stack / Desktop',
    department: 'Engineering',
    location: 'Hybrid (NYC preferred) / Remote (US)',
    remote: true,
    employmentType: 'Full-time',
    tagline:
      'Build the visual design studio and the always-on operations portal that regulated enterprises use to author and run their AI agents.',
    about: [
      "CriticalBridge is an enterprise platform for building, governing, and running AI agents and automated workflows in regulated industries — starting with insurance. Every large enterprise is now accumulating AI agents faster than it can govern or connect them, and that sprawl has become a board-level problem with hard regulatory deadlines attached. We're the neutral control plane that lets organizations design, deploy, and govern agents — native and third-party — inside their own security perimeter.",
      "We're seed-stage, founded by operators who've led IT and underwriting inside the world's largest carriers, with live pilots underway at Tier-1 global insurers. You'd be one of our first engineering hires, with foundational ownership of everything our users see and touch.",
    ],
    summary:
      "You'll own our two human-facing surfaces. The desktop studio (Electron) is the design-time environment where builders author agents, workflows, and document-ingestion pipelines on a visual node canvas. The Agent Portal (Next.js on Vercel) is the always-on, multi-tenant operations plane — work queues, SLA tracking, human-in-the-loop approval gates, license utilization, and audit drill-down — that keeps running with no desktop open. The principle is design in the desktop, run in the cloud, monitor from anywhere, and you'll build both ends of it.",
    howWeWork:
      'TypeScript strict mode with zero-error gates. One-issue / one-branch / one-PR, with small, focused PRs (~10 files / 500 lines) and short-lived branches. Branch-protected main with required typecheck gates. Pre-commit linting and formatting (husky, lint-staged, ESLint, Prettier). Security and auditability are first-class, not afterthoughts.',
    responsibilities: [
      'Build complex, data-dense enterprise UI: the React Flow visual workflow canvas, schema-driven configuration panels (auto-rendered from JSON Schema), compliance dashboards, and the Monaco-based code surfaces.',
      'Develop the Agent Portal: real-time work queues and process monitors (Pub/Sub → SSE push), HITL approval consoles, license/oversubscription views, and audit/event drill-down with decision lineage and citations.',
      "Own the Electron application and its hardened security model — renderer sandboxing, careful credential handling, and secure communication with the customer's cloud environment.",
      'Build and maintain the node/connector framework — hundreds of pre-built operations rendered into config UI automatically, with conditional fields and connector routing.',
      "Integrate against the Brain's HTTP API end-to-end (you'll touch backend boundaries — this is a full-stack role).",
      'Care deeply about performance, accessibility, internationalization (all strings keyed), and the polish that makes enterprise software feel trustworthy.',
    ],
    requirements: [
      '8+ years building production web/desktop applications with TypeScript and React.',
      'Strong command of modern React patterns and state management (we use Redux Toolkit, TanStack Query/Table, React Hook Form, Zod).',
      'Experience building complex, interactive UIs — visual editors, canvases, dashboards, or data-heavy tooling (React Flow, D3, or similar a strong plus).',
      'Comfort working full-stack: consuming and shaping APIs, real-time updates (SSE/WebSockets), and reasoning about the client/server boundary.',
      'An eye for UX and craft, and the discipline to ship clean, typed, well-tested code.',
    ],
    niceToHave: [
      'Electron or other desktop-application experience, especially its security model.',
      'Next.js on Vercel; SSE / real-time UI.',
      'Node-based / visual-programming or low-code builder experience.',
      'Enterprise or regulated-industry product experience (audit, RBAC, SSO, compliance UX).',
    ],
    stack: [
      { label: 'Language', items: 'TypeScript (strict), Node.js' },
      {
        label: 'Desktop',
        items:
          'Electron, React 18, shadcn/ui on Radix, Tailwind, Lucide, React Flow (node canvas), Monaco, ECharts / Recharts / D3, TanStack Query/Table, React Hook Form, Zod/Ajv, Redux Toolkit + Redux Persist, i18next, electron-store, SQLite',
      },
      { label: 'Web', items: 'Next.js on Vercel, Auth0, SSE' },
      {
        label: "Backend you'll touch",
        items: 'Node/TypeScript services on Cloud Run, Pub/Sub, Cloud SQL (Postgres), GCS',
      },
      { label: 'Identity', items: 'Auth0 (SSO/SAML, OAuth 2.0 / PKCE)' },
    ],
    compensation: {
      min: 170000,
      max: 210000,
      currency: 'USD',
      equity: true,
      equityNote: 'Meaningful equity from the first option pool',
      salaryNote:
        "The final base salary offer will depend on the candidate's geographic location, experience, and skill level.",
      benefits: [
        'Comprehensive health, dental, and vision insurance',
        'Paid Time Off (PTO) and company holidays',
      ],
    },
    updatedAt: '2026-07-20',
  },
  {
    id: 'forward-field-engineer',
    title: 'Forward Field Engineer',
    department: 'Forward Deployed',
    location: 'Hybrid/Remote + customer site travel',
    remote: true,
    employmentType: 'Full-time',
    tagline:
      "Embed with the world's largest insurers to stand up CriticalBridge in production — from first integration to a live, governed agent workflow.",
    about: [
      "CriticalBridge is an enterprise platform for building, governing, and running AI agents and automated workflows in regulated industries — starting with insurance. Every large enterprise is now accumulating AI agents faster than it can govern or connect them, and that sprawl has become a board-level problem with hard regulatory deadlines attached. We're the neutral control plane that lets organizations design, deploy, and govern agents inside their own security perimeter.",
      "We're seed-stage, founded by operators who've led IT and underwriting inside the world's largest carriers, with live pilots underway at Tier-1 global insurers. Forward Field Engineers are how those pilots become production.",
    ],
    summary:
      "You are the technical face of CriticalBridge on the ground. You'll own the end-to-end technical delivery of customer deployments — standing up each customer's environment, integrating with their legacy systems, building their agents and workflows in our studio, and turning a scoping session into a live, governed automation in production. You'll work shoulder-to-shoulder with our Forward Domain Experts (who own the business side) and the customer's own IT and business-analyst teams.",
    howWeWork:
      'TypeScript strict mode with zero-error gates. One-issue / one-branch / one-PR, with small, focused PRs and short-lived branches. Branch-protected main with required typecheck gates. Pre-commit linting and formatting. Security and auditability are first-class, not afterthoughts.',
    responsibilities: [
      'Own end-to-end technical delivery of customer deployments — from environment stand-up to a live, governed workflow running in production.',
      'Integrate CriticalBridge with carrier systems — including claims platforms, external data providers, internal tools and knowledge sources, email, communication platforms, multiple policy-admin systems — via APIs, connectors, and data pipelines.',
      "Build and configure agents, workflows, and document-ingestion pipelines in the Agent studio for each customer's Claims and Underwriting use cases.",
      "Configure compliance and governance controls — risk tiers, human-in-the-loop approval gates, and audit — to each customer's requirements.",
      'Run technical workshops, troubleshoot live issues, and keep pilots and operational releases on schedule and to scope.',
      "Partner with Forward Domain Experts and the customer's IT/BA teams throughout the delivery lifecycle.",
      'Be the voice of the field back to product and engineering — turning deployment friction into product improvements and reusable delivery assets.',
    ],
    requirements: [
      '10+ years in a client-facing engineering, solutions-engineering, implementation, or forward-deployed role — equally comfortable in code and in front of a customer.',
      'Strong TypeScript / JavaScript / Node and hands-on API integration; comfortable scripting, debugging, and reading an unfamiliar system.',
      'Real systems-integration experience connecting SaaS platforms to enterprise or legacy systems (REST, webhooks, message queues, ETL / data pipelines, OAuth and auth flows).',
      'Working knowledge of cloud (GCP a plus), SQL, and data modeling.',
      'Excellent communication and composure under pressure — you can own a room of customer stakeholders and a production incident in the same afternoon.',
      'Willingness to travel to customer sites.',
    ],
    niceToHave: [
      'Insurance-vertical exposure (P&C claims or underwriting) — though our Forward Domain Experts own the deep domain.',
      'Experience integrating with core insurance platforms (Guidewire, Duck Creek, Insurity, Majesco, etc.).',
      'AI/LLM systems: agents, RAG, embeddings, prompt/eval workflows.',
      'Electron / desktop or Next.js familiarity.',
    ],
    stack: [
      { label: 'Language', items: 'TypeScript (strict), Node.js' },
      {
        label: 'Cloud (GCP-native)',
        items:
          'Cloud Run, Pub/Sub, Cloud Tasks, Cloud SQL (Postgres), Cloud Storage, Secret Manager, IAM',
      },
      {
        label: 'Platform surfaces',
        items:
          'Electron desktop studio (React, React Flow), Agent Portal (Next.js on Vercel), provider-abstracted LLMs (OpenAI / Anthropic / Google), Supabase (pgVector) RAG, Neo4j Aura knowledge graph',
      },
      {
        label: 'Integration',
        items: 'REST APIs, webhooks, connectors, data pipelines; Auth0 (SSO/SAML, OAuth 2.0)',
      },
    ],
    compensation: {
      min: 170000,
      max: 215000,
      currency: 'USD',
      equity: true,
      salaryNote:
        "The final base salary offer will depend on the candidate's geographic location, experience, and skill level.",
      benefits: [
        'Performance-based annual cash bonus',
        'Company equity/stock option incentives',
        'Comprehensive health, dental, and vision insurance',
        'Paid Time Off (PTO) and company holidays',
      ],
    },
    updatedAt: '2026-07-16',
  },
  {
    id: 'forward-domain-expert',
    title: 'Forward Domain Expert — P&C Insurance',
    department: 'Forward Deployed',
    location: 'Hybrid/Remote + customer site travel',
    remote: true,
    employmentType: 'Full-time',
    tagline:
      'Turn how underwriters and claims teams actually work into governed AI agent workflows — embedded with the carriers deploying them.',
    about: [
      "CriticalBridge is an enterprise platform for building, governing, and running AI agents and automated workflows in regulated industries — starting with insurance. Every large enterprise is now accumulating AI agents faster than it can govern or connect them, and that sprawl has become a board-level problem with hard regulatory deadlines attached. We're the neutral control plane that lets organizations design, deploy, and govern agents inside their own security perimeter.",
      "We're seed-stage, founded by operators who've led underwriting, IT, and transformation inside the world's largest carriers, with live pilots underway at Tier-1 global insurers.",
    ],
    summary:
      "You are the insurance domain authority on our client deployments — the person who understands a carrier's underwriting or claims operation deeply enough to redesign it as agentic workflows, and who can lead the business through the change. You sit between the customer's business subject-matter experts and our Forward Field Engineers, owning the solution from discovery to a working, value-proven deployment. This is a hybrid product-manager / domain-expert role: part solutioning, part delivery, part product feedback. Your judgment about how P&C underwriting and claims really work is what makes our automations trustworthy to the people who run them.",
    howWeWork:
      'Small, senior, and forward-deployed. We solution in the open with customers, keep scope disciplined, and treat auditability and compliance as first-class — because our customers are regulated and our automations touch real underwriting and claims decisions.',
    responsibilities: [
      'Serve as the P&C domain authority across client deployments — translating real underwriting and claims processes into CriticalBridge agent and workflow designs.',
      'Lead discovery and scoping workshops with carrier business SMEs and IT business analysts; map current-state processes, roles, metrics, data, and core systems, and define the target-state agentic workflow.',
      'Own solution deliverables, requirements, user stories, and scope — managed in Jira and Confluence — and keep deployments on track and in scope.',
      'Partner with Forward Field Engineers to turn domain designs into deployed, governed configurations, and validate that they reflect real practice.',
      'Ground solutions in segment-specific realities (e.g., commercial and specialty lines, personal lines) and optimal account or claim handling — submission intake, triage & assignment, risk appetite, risk assessment, referral/authority, rating and pricing, quote proposal, policy issuance, account servicing, straight-through vs. guided underwriting for underwriting; FNOL, claim set-up, triage & assignment, evaluation and investigation, reserving, litigation, resolution & adjudication, payments, subrogation, and recovery for claims.',
      'Provide detailed expertise on the underwriting or claims entity model to support product enhancements or customer solutions.',
      'Work with customers and the product team to design user capabilities for workbench solutions (underwriting, claims).',
      'Define and track the metrics that prove value — loss and combined ratio impact, new business hit ratios, renewal retention ratios, underwriting leakage, quote turnaround timing, claim settlement cycle time, claim leakage.',
      'Feed domain requirements into the product roadmap, and help build the reference-account narrative from each successful pilot and deployed solution.',
    ],
    requirements: [
      'Minimum 10–15 years as an insurance software product manager or senior member of a software product team, in the P&C vertical.',
      "Deep, hands-on expertise in P&C underwriting or claims — you've built and delivered solutions in these functions, not just observed them.",
      'Detailed command of the processes, roles, metrics, data, core applications, and standalone tools used across underwriting and/or claims.',
      'First-hand experience with P&C insurance products, especially commercial and specialty lines.',
      'Extensive experience working with insurance business SMEs and IT BAs across the full SDLC — leading working sessions, developing deliverables, solutioning with customers, and managing scope — using standard tools (Jira, Confluence).',
      'Demonstrated experience partnering with engineers to deliver solutions at an established software firm in the insurance vertical.',
      'Excellent facilitation and written communication; credible in front of carrier business and technology leaders.',
    ],
    niceToHave: [
      'Exposure to both underwriting and claims; underwriting-workbench or straight-through-processing experience.',
      'Familiarity with core platforms (Guidewire, Duck Creek, Majesco, Insurity) and rating / rules engines.',
      'Experience with AI, agentic, or automation solutions in insurance workflows.',
      'Working knowledge of insurance regulation (e.g., NAIC AI guidance) and its impact on solution design.',
    ],
    stackHeading: "What you'll work with",
    stack: [
      {
        label: 'Platform',
        items: 'Agent Studio, Lighthouse, Brain, Agent Console — your solution canvas.',
      },
      {
        label: 'Integration targets',
        items:
          'Carrier core systems (policy-admin and claims platforms), alongside our Forward Field Engineers.',
      },
      {
        label: 'Delivery',
        items:
          'Jira and Confluence, plus the working-session and deliverable cadence you already know.',
      },
    ],
    compensation: {
      min: 180000,
      max: 225000,
      currency: 'USD',
      equity: true,
      salaryNote:
        "The final base salary offer will depend on the candidate's geographic location, experience, and skill level.",
      benefits: [
        'Performance-based annual cash bonus',
        'Company equity/stock option incentives',
        'Comprehensive health, dental, and vision insurance',
        'Paid Time Off (PTO) and company holidays',
      ],
    },
    updatedAt: '2026-07-16',
  },
];

export function findRoleById(id: string): Role | undefined {
  return roles.find((r) => r.id === id);
}

export function formatSalaryRange(comp?: RoleCompensation): string | null {
  if (!comp?.min || !comp?.max) return null;
  const fmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: comp.currency || 'USD',
    maximumFractionDigits: 0,
  });
  return `${fmt.format(comp.min)} – ${fmt.format(comp.max)}`;
}

export function formatCompensation(comp?: RoleCompensation): string | null {
  if (!comp) return null;
  const range = formatSalaryRange(comp);
  const parts: string[] = [];
  if (range) parts.push(range);
  if (comp.equity) parts.push('+ equity');
  return parts.length > 0 ? parts.join(' ') : comp.notes || null;
}
